import {
  FieldArrayPath,
  FieldPath,
  FieldValues,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  UseFieldArrayReturn,
  useForm,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {DevTool} from '@hookform/devtools';
import {ReadOnlyContext} from './readonly-context';
import {ZodSchema} from 'zod';
import {SchemaProvider} from './use-schema-context';
import {zodResolver} from '@hookform/resolvers/zod';
import {entries, keys, values} from 'lodash';
import {Response} from '../api/generated/index.defs';
import {mapErrors} from './map-formal-errors';
import {
  Alert,
  Button,
  ButtonProps,
  Group,
  GroupProps,
  Paper,
  PaperProps,
  Text,
} from '@mantine/core';
import {ClassNames} from '@emotion/react';
import {ErrorMessage} from '@hookform/error-message';

type FormProps<
  TFieldValues extends FieldValues,
  TSchema extends ZodSchema<TFieldValues> = ZodSchema<TFieldValues>,
> = {
  onSubmit?: SubmitHandler<TFieldValues>;
  children: ((methods: UseFormReturn<TFieldValues>) => ReactNode) | ReactNode;
  debug?: boolean;
  readOnly?: boolean;
  schema: TSchema;
  className?: string;
} & Omit<UseFormProps<TFieldValues>, 'resolver' | 'values'>;

const SERVER_ERROR = 'root.serverError';

export const $HookForm = <TFieldValues extends FieldValues>(
  props: FormProps<TFieldValues>
) => {
  const {
    onSubmit,
    children,
    debug,
    readOnly = false,
    schema,
    className,
    ...useFormProps
  } = props;

  const resolver = zodResolver(schema);
  const methods = useForm<TFieldValues>({
    resolver,
    ...useFormProps,
  });

  const _onSubmit = useCallback<SubmitHandler<TFieldValues>>(
    async (formValues) => {
      if (!onSubmit) return;

      const response = await onSubmit(formValues);

      if (response && (response as Response<never>).hasErrors) {
        const fields = keys(methods.getValues());
        const errors = mapErrors(response as Response<never>, fields);

        entries(errors).forEach(([name, error]) => {
          if (name === '_UNKNOWN_') {
            methods.setError(SERVER_ERROR, {
              type: 'Server Error',
              message: typeof error === 'string' ? error : values(error)[0],
            });

            return;
          }
          methods.setError(name as FieldPath<TFieldValues>, {message: error});
        });

        return;
      }

      return response;
    },
    [methods, onSubmit]
  );

  const readonlyContextValue = useMemo(() => ({readOnly}), [readOnly]);

  return (
    <SchemaProvider schema={schema}>
      <FormProvider {...methods}>
        <ReadOnlyContext.Provider value={readonlyContextValue}>
          <ClassNames>
            {({cx}) => (
              <form
                onSubmit={methods.handleSubmit(_onSubmit)}
                className={cx('hook-form', className)}
              >
                {typeof children === 'function' ? children(methods) : children}
              </form>
            )}
          </ClassNames>
        </ReadOnlyContext.Provider>
      </FormProvider>
      {debug && (
        <DevTool control={methods.control} placement={'bottom-right'} />
      )}
    </SchemaProvider>
  );
};

export const $ServerError = () => {
  return (
    <ErrorMessage
      name={SERVER_ERROR}
      render={({message}) => (
        <Alert color={'red'} mb={'sm'}>
          <Text fz={14}>{message}</Text>
        </Alert>
      )}
    />
  );
};

export const $FormActions: FC<PropsWithChildren> = ({children}) => {
  const {readOnly} = useContext(ReadOnlyContext);
  return readOnly ? null : <>{children}</>;
};

type FormSubmitProps = Omit<ButtonProps, 'type'>;
export const $FormSubmit: FC<FormSubmitProps> = (props) => {
  const {disabled, children = 'Submit', ...rest} = props;
  const {readOnly} = useContext(ReadOnlyContext);

  return (
    <Button
      type="submit"
      disabled={readOnly || disabled}
      children={children}
      {...rest}
    />
  );
};

const FormRowDefaultProps: GroupProps = {
  align: 'flex-start',
  grow: true,
  wrap: 'nowrap',
};
export const $FormRow: FC<GroupProps> = (props) => {
  return <Group {...FormRowDefaultProps} {...props} />;
};

export const $FormContainer: FC<PropsWithChildren<PaperProps>> = (props) => {
  const {
    children,
    py = 16,
    px = 16,
    withBorder = true,
    shadow = 'sm',
    ...rest
  } = props;

  return (
    <Paper withBorder={withBorder} shadow={shadow} py={py} px={px} {...rest}>
      {children}
    </Paper>
  );
};

type FieldArrayHelpers<
  TFieldValues extends FieldValues,
  TFieldArrayName extends
    FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
> = UseFieldArrayReturn<TFieldValues, TFieldArrayName> & {
  moveUp: (index: number) => void;
  moveDown: (index: number) => void;
};

type FieldArrayProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends
    FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
> = {
  name: TFieldArrayName;
  children: (
    fieldArrayHelpers: FieldArrayHelpers<TFieldValues, TFieldArrayName>
  ) => ReactNode;
};
export const $FieldArray = <
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends
    FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
>(
  props: FieldArrayProps<TFieldValues, TFieldArrayName>
) => {
  const {name, children} = props;
  const fieldArray = useFieldArray<TFieldValues, TFieldArrayName>({name});

  const moveUp = useCallback(
    (index: number) => {
      if (index > 0) {
        fieldArray.swap(index, index - 1);
      }
    },
    [fieldArray]
  );

  const moveDown = useCallback(
    (index: number) => {
      if (index < fieldArray.fields.length - 1) {
        fieldArray.swap(index, index + 1);
      }
    },
    [fieldArray]
  );

  const helpers = useMemo(
    () => ({...fieldArray, moveUp, moveDown}),
    [fieldArray, moveDown, moveUp]
  );

  return children(helpers);
};